// API service for communicating with the backend
import useAuthStore from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get auth headers from store
    const authHeaders = useAuthStore.getState().getAuthHeader();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        // Only auto-logout if user was previously authenticated (expired session)
        // Don't redirect on login/register failures
        if (useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Roadmap CRUD operations
  async getAllRoadmaps() {
    const response = await this.request('/roadmaps');
    return response.data;
  }

  async getRoadmap(id) {
    const response = await this.request(`/roadmaps/${id}`);
    return response; // Return full response to access userAccessLevel
  }

  async createRoadmap(roadmapData) {
    const response = await this.request('/roadmaps', {
      method: 'POST',
      body: JSON.stringify(roadmapData),
    });
    return response.data;
  }

  async updateRoadmap(id, roadmapData) {
    const response = await this.request(`/roadmaps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roadmapData),
    });
    return response.data;
  }

  async deleteRoadmap(id) {
    await this.request(`/roadmaps/${id}`, {
      method: 'DELETE',
    });
    return true;
  }

  // Step operations
  async getStepData(roadmapId, stepName) {
    const response = await this.request(`/roadmaps/${roadmapId}/step/${stepName}`);
    return response.data;
  }

  async updateStepData(roadmapId, stepName, stepData) {
    const response = await this.request(`/roadmaps/${roadmapId}/step/${stepName}`, {
      method: 'PUT',
      body: JSON.stringify(stepData),
    });
    return response.data;
  }

  async markStepComplete(roadmapId, stepName, completed) {
    const response = await this.request(`/roadmaps/${roadmapId}/step/${stepName}/complete`, {
      method: 'PATCH',
      body: JSON.stringify({ completed }),
    });
    return response.data;
  }

  // Project sharing operations
  async getRoadmapByProjectId(projectId) {
    const response = await this.request(`/roadmaps/project/${projectId}`);
    return response; // Return full response to access message
  }

  async shareRoadmap(roadmapId, email, accessLevel = 'edit') {
    const response = await this.request(`/roadmaps/${roadmapId}/share`, {
      method: 'POST',
      body: JSON.stringify({ email, access_level: accessLevel }),
    });
    return response.data;
  }

  async removeSharedUser(roadmapId, userId) {
    await this.request(`/roadmaps/${roadmapId}/share/${userId}`, {
      method: 'DELETE',
    });
    return true;
  }

  async updateSharedUserAccess(roadmapId, userId, accessLevel) {
    const response = await this.request(`/roadmaps/${roadmapId}/share/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ access_level: accessLevel }),
    });
    return response.data;
  }

  async getSharedUsers(roadmapId) {
    const response = await this.request(`/roadmaps/${roadmapId}/shared-users`);
    return response.data;
  }
  // AI operations
  async generateRoadmap(description, projectType, language) {
    const response = await this.request('/ai/generate-roadmap', {
      method: 'POST',
      body: JSON.stringify({ description, projectType, language }),
    });
    return response.data;
  }

  async suggestStep(roadmapId, stepName, language) {
    const response = await this.request(`/ai/suggest/${stepName}`, {
      method: 'POST',
      body: JSON.stringify({ roadmapId, language }),
    });
    return response.data;
  }

  async critiqueRoadmap(roadmapId, language) {
    const response = await this.request(`/ai/critique/${roadmapId}`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    });
    return response.data;
  }

  async marketScan(description, industry, language) {
    const response = await this.request('/ai/market-scan', {
      method: 'POST',
      body: JSON.stringify({ description, industry, language }),
    });
    return response.data;
  }

  async riskScan(description, industry, existingRisks, language) {
    const response = await this.request('/ai/risk-scan', {
      method: 'POST',
      body: JSON.stringify({ description, industry, existingRisks, language }),
    });
    return response.data;
  }

  async competitiveBrief(description, competitors, language) {
    const response = await this.request('/ai/competitive-brief', {
      method: 'POST',
      body: JSON.stringify({ description, competitors, language }),
    });
    return response.data;
  }

  async exportNarrative(roadmapId, language) {
    const response = await this.request('/ai/export-narrative', {
      method: 'POST',
      body: JSON.stringify({ roadmapId, language }),
    });
    return response.data;
  }

  async generateSmartGoal(idea, roadmapId, language) {
    const response = await this.request('/ai/smart-goal/generate', {
      method: 'POST',
      body: JSON.stringify({ idea, roadmapId, language }),
    });
    return response.data;
  }

  async coachSmartGoal(goal, roadmapId, language) {
    const response = await this.request('/ai/smart-goal/coach', {
      method: 'POST',
      body: JSON.stringify({ goal, roadmapId, language }),
    });
    return response.data;
  }

  async aiChat(messages, roadmapId, language) {
    const response = await this.request('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, roadmapId, language }),
    });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
